import { useEffect, useRef, useState } from 'react';

const SEGMENTS = 10;

/**
 * Custom cursor from the Artesã Design System: ring + dot + trail.
 * Public/marketing pages only — never inside the app.
 */
const PointerCursor = () => {
  const ringRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const segRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const finePointer = window.matchMedia('(pointer: fine)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setEnabled(finePointer && !reduced);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let targetX = 0;
    let targetY = 0;
    let curX = 0;
    let curY = 0;
    let active = false;
    let raf = 0;
    const segX = new Array(SEGMENTS).fill(0);
    const segY = new Array(SEGMENTS).fill(0);

    const handleMove = (e: PointerEvent) => {
      targetX = e.clientX;
      targetY = e.clientY;
      if (!active) {
        curX = targetX;
        curY = targetY;
        for (let i = 0; i < SEGMENTS; i++) {
          segX[i] = targetX;
          segY[i] = targetY;
        }
      }
      active = true;
    };

    const handleLeave = () => {
      active = false;
    };

    const loop = () => {
      const ring = ringRef.current;
      const dot = dotRef.current;
      if (ring && dot) {
        if (active) {
          curX += (targetX - curX) * 0.25;
          curY += (targetY - curY) * 0.25;
          ring.style.transform = `translate3d(${curX}px, ${curY}px, 0) translate(-50%, -50%)`;
          dot.style.transform = `translate3d(${targetX}px, ${targetY}px, 0) translate(-50%, -50%)`;
          ring.style.opacity = '1';
          dot.style.opacity = '1';

          segX[0] += (targetX - segX[0]) * 0.3;
          segY[0] += (targetY - segY[0]) * 0.3;
          for (let i = 1; i < SEGMENTS; i++) {
            segX[i] += (segX[i - 1] - segX[i]) * 0.3;
            segY[i] += (segY[i - 1] - segY[i]) * 0.3;
          }
          for (let j = 0; j < SEGMENTS; j++) {
            const el = segRefs.current[j];
            if (el) {
              el.style.transform = `translate3d(${segX[j]}px, ${segY[j]}px, 0) translate(-50%, -50%)`;
              el.style.opacity = '1';
            }
          }
        } else {
          ring.style.opacity = '0';
          dot.style.opacity = '0';
          segRefs.current.forEach((el) => {
            if (el) el.style.opacity = '0';
          });
        }
      }
      raf = window.requestAnimationFrame(loop);
    };

    window.addEventListener('pointermove', handleMove, { passive: true });
    document.addEventListener('mouseleave', handleLeave);
    raf = window.requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      document.removeEventListener('mouseleave', handleLeave);
      window.cancelAnimationFrame(raf);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div className="ds-cursor" aria-hidden="true">
      {Array.from({ length: SEGMENTS }).map((_, i) => {
        const size = Math.max(3, 9 - i * 0.6);
        return (
          <div
            key={i}
            ref={(el) => {
              segRefs.current[i] = el;
            }}
            className="cz-seg"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              background: `hsl(var(--rose-500) / ${Math.max(0.15, 0.7 - i * 0.06)})`,
            }}
          />
        );
      })}
      <div ref={ringRef} className="cz-ring" />
      <div ref={dotRef} className="cz-dot" />
    </div>
  );
};

export default PointerCursor;
